/**
 * Pure board logic for the "Arrange Hierarchy" drag-and-drop modal
 * (WorkRolesPage → HierarchyModal). Extracted from the component so the
 * transforms can be unit-tested without a DOM or dnd-kit — the drag UI was a
 * source of intermittent "Something went wrong" crashes, and the invariant
 * that guards against them (every role lives in exactly ONE container, so React
 * never sees a duplicate key) is only meaningful if it can be asserted.
 *
 * A "board" is `{ levels: [{ id, roles: [{id,name,rank,...}] }], unranked: [role] }`.
 * A "container" is a level id, or the string 'unranked'.
 *
 * Every transform is PURE: it returns a NEW board (or the same reference when
 * nothing changes) and never mutates its input.
 */
import { arrayMove } from '@dnd-kit/sortable'

export const byNameCmp = (a, b) => a.name.localeCompare(b.name)

/** Group ranked roles into ordered hierarchy LEVELS (Level 1 = most senior).
 *  Roles sharing a `rank` become peers in one level. Each level gets a stable
 *  synthetic id so a drag targets a level regardless of re-indexing. */
export function seedLevels(roles) {
  const ranked = (roles ?? []).filter((r) => typeof r.rank === 'number')
  const byRank = new Map()
  for (const r of ranked) {
    if (!byRank.has(r.rank)) byRank.set(r.rank, [])
    byRank.get(r.rank).push(r)
  }
  return [...byRank.keys()]
    .sort((a, b) => a - b)
    .map((k, i) => ({
      id: `seed-${i}`,
      roles: byRank.get(k).sort(byNameCmp),
    }))
}

/** The initial board for a set of roles: ranked → levels, the rest → unranked. */
export function seedBoard(roles) {
  return {
    levels: seedLevels(roles),
    unranked: (roles ?? [])
      .filter((r) => typeof r.rank !== 'number')
      .sort(byNameCmp),
  }
}

/** Which container holds `id` (a role id OR a container id). null if unknown. */
export function containerOf(board, id) {
  if (id === 'unranked' || board.levels.some((l) => l.id === id)) return id
  const lvl = board.levels.find((l) => l.roles.some((r) => r.id === id))
  if (lvl) return lvl.id
  if (board.unranked.some((r) => r.id === id)) return 'unranked'
  return null
}

export function rolesIn(board, c) {
  return c === 'unranked'
    ? board.unranked
    : (board.levels.find((l) => l.id === c)?.roles ?? [])
}

export function withContainer(board, c, nextRoles) {
  if (c === 'unranked') return { ...board, unranked: nextRoles }
  return {
    ...board,
    levels: board.levels.map((l) =>
      l.id === c ? { ...l, roles: nextRoles } : l,
    ),
  }
}

/**
 * Live cross-container reparent during a drag (dnd-kit onDragOver).
 * Returns `{ board, reparented }`. `reparented` is true only when a role
 * actually moved to a different container — the caller uses it to suppress the
 * within-container sort on dragEnd.
 *
 * No-ops (returning the same board) when: there's no target, the pointer is
 * over the dragged card itself, either container can't be resolved, or the
 * target is the SAME container (that's a reorder, handled on dragEnd).
 */
export function applyDragOver(board, activeId, overId) {
  if (overId == null || activeId === overId) return { board, reparented: false }
  const from = containerOf(board, activeId)
  const to = containerOf(board, overId)
  if (!from || !to || from === to) return { board, reparented: false }
  const fromRoles = rolesIn(board, from)
  const toRoles = rolesIn(board, to)
  const moving = fromRoles.find((r) => r.id === activeId)
  if (!moving) return { board, reparented: false }
  const overIdx =
    overId === to ? toRoles.length : toRoles.findIndex((r) => r.id === overId)
  const insertAt = overIdx < 0 ? toRoles.length : overIdx
  let next = withContainer(
    board,
    from,
    fromRoles.filter((r) => r.id !== activeId),
  )
  next = withContainer(next, to, [
    ...toRoles.slice(0, insertAt),
    moving,
    ...toRoles.slice(insertAt),
  ])
  return { board: next, reparented: true }
}

/**
 * Within-container reorder on drop (dnd-kit onDragEnd). Only reorders when the
 * drag started and ended in the SAME container (a cross-container move already
 * happened live in applyDragOver). Returns the same board when nothing moves.
 */
export function applyDragEnd(board, activeId, overId) {
  if (overId == null) return board
  const from = containerOf(board, activeId)
  const to = containerOf(board, overId)
  if (!from || !to || from !== to) return board
  const arr = rolesIn(board, from)
  const oldI = arr.findIndex((r) => r.id === activeId)
  const newI = arr.findIndex((r) => r.id === overId)
  if (oldI < 0 || newI < 0 || oldI === newI) return board
  return withContainer(board, from, arrayMove(arr, oldI, newI))
}

/** Accessible fallback for drag: move `role` to `dest` (a level id or
 *  'unranked') via the per-card selector. Removes it from wherever it was
 *  first, so it can never end up in two containers. */
export function moveRoleTo(board, role, dest) {
  const levels = board.levels.map((l) => ({
    ...l,
    roles: l.roles.filter((r) => r.id !== role.id),
  }))
  let unranked = board.unranked.filter((r) => r.id !== role.id)
  if (dest === 'unranked') {
    unranked = [...unranked, role].sort(byNameCmp)
  } else {
    const i = levels.findIndex((l) => l.id === dest)
    if (i >= 0) {
      levels[i] = { ...levels[i], roles: [...levels[i].roles, role].sort(byNameCmp) }
    }
  }
  return { levels, unranked }
}

/** Append an empty level with a fresh synthetic id. */
export function addLevel(board, newId) {
  return { ...board, levels: [...board.levels, { id: newId, roles: [] }] }
}

/** Drop a level, but only if it's empty (a populated level is never removed). */
export function removeLevel(board, levelId) {
  return {
    ...board,
    levels: board.levels.filter((l) => l.id !== levelId || l.roles.length > 0),
  }
}

/**
 * The PATCH /work-roles/reorder payload: non-empty levels (most senior first),
 * each an array of role ids. Unranked roles are omitted → the backend sets
 * them rank null. Only real role ids are emitted — never a level/container id.
 */
export function savePayload(board) {
  return board.levels
    .filter((l) => l.roles.length > 0)
    .map((l) => l.roles.map((r) => r.id))
}
